import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

export const PromoBannerCarousel = ({ banners, variant = 'default' }) => {
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);
  const pausedRef = useRef(false);

  const scrollToSlide = useCallback((idx) => {
    if (!scrollRef.current) return;
    const slideWidth = scrollRef.current.offsetWidth;
    scrollRef.current.scrollTo({ left: idx * slideWidth, behavior: 'smooth' });
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!banners || banners.length <= 1) return;
    const tick = () => {
      if (pausedRef.current) return;
      setCurrent(prev => {
        const next = (prev + 1) % banners.length;
        scrollToSlide(next);
        return next;
      });
    };
    timerRef.current = setInterval(tick, 4000);
    return () => clearInterval(timerRef.current);
  }, [banners, scrollToSlide]);

  // Sync dots with actual scroll position
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, offsetWidth } = scrollRef.current;
    const idx = Math.round(scrollLeft / offsetWidth);
    setCurrent(idx);
  }, []);

  // Pause auto-advance on interaction
  const handleInteractionStart = useCallback(() => {
    pausedRef.current = true;
  }, []);
  const handleInteractionEnd = useCallback(() => {
    setTimeout(() => { pausedRef.current = false; }, 3000);
  }, []);

  const goToSlide = (idx) => {
    setCurrent(idx);
    scrollToSlide(idx);
    pausedRef.current = true;
    setTimeout(() => { pausedRef.current = false; }, 3000);
  };

  if (!banners || banners.length === 0) return null;

  const borderRadius = variant === 'blinkit' ? 'rounded-xl' : 'rounded-2xl';

  const BannerContent = ({ banner }) => {
    const inner = (
      <div
        className={`relative w-full ${borderRadius} overflow-hidden`}
        style={{ backgroundColor: banner.bg_color || '#f0f0f0', minHeight: 120 }}
        data-testid="promo-banner-slide"
      >
        <img
          src={banner.image_url}
          alt={banner.title || 'promo'}
          className="w-full h-36 sm:h-44 md:h-52 object-cover"
          onError={e => { e.target.style.display = 'none'; }}
        />
        {(banner.title || banner.subtitle) && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
            {banner.title && (
              <p className="font-bold text-sm" style={{ color: banner.text_color || '#fff' }}>{banner.title}</p>
            )}
            {banner.subtitle && (
              <p className="text-xs opacity-90" style={{ color: banner.text_color || '#fff' }}>{banner.subtitle}</p>
            )}
          </div>
        )}
      </div>
    );
    if (banner.link_url) {
      if (banner.link_url.startsWith('http')) {
        return <a href={banner.link_url} target="_blank" rel="noreferrer">{inner}</a>;
      }
      return <Link to={banner.link_url}>{inner}</Link>;
    }
    return inner;
  };

  if (banners.length === 1) {
    return (
      <div className="px-4 mb-4" data-testid="promo-banner-carousel">
        <BannerContent banner={banners[0]} />
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="promo-banner-carousel">
      {/* Slides — no gap, each slide is exactly container width */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onTouchStart={handleInteractionStart}
        onTouchEnd={handleInteractionEnd}
        onMouseDown={handleInteractionStart}
        onMouseUp={handleInteractionEnd}
        className="flex overflow-x-auto snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {banners.map((banner, i) => (
          <div key={banner.id || i} className="w-full flex-shrink-0 snap-center px-4">
            <BannerContent banner={banner} />
          </div>
        ))}
      </div>

      {/* Dots — always centered */}
      <div className="flex justify-center items-center gap-1.5 mt-2.5">
        {banners.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`rounded-full transition-all duration-300 ${
              i === current
                ? 'w-5 h-2 bg-[var(--theme-primary)]'
                : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
            }`}
            data-testid={`promo-dot-${i}`}
            aria-label={`Go to banner ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
